import type { FeatureFlagValue } from '@posthog/core';
import IFlagsmith from 'flagsmith';
import { Flags } from 'flagsmith-nodejs';
import { PostHog } from 'posthog-js';
import React from 'react';

enum FlagsmithFeatureFlagMap {
  // These two are permanent flags, we use these to rollout flags
  // to our dogfood and launchpad segments.
  EnableAlphaApps = 'enable_alpha_apps',
  EnableBetaApps = 'enable_beta_apps',

  // This is permanently enabled, we no longer check Flagsmith for this flag
  EnableManyCompanies = 'enable_many_companies',

  EnableFolderLocking = 'enable_folder_locking',
  EnableRootFolderLocking = 'enable_root_folder_locking',
  EnableSubscriptionEditSummary = 'subscription_edit_summary',
  EnableAppSetup = 'enable_app_setup',
  EnableAppSetupUIUpdates = 'enable_app_setup_ui_updates',
}

enum PostHogFeatureFlagMap {
  EnableAppSetupClientPreview = 'app-setup-client-sidebar-preview',
  EnableBillingConsolidation = 'billing-consolidation',
  EnableInvoiceStatusNotifications = 'enable-invoice-status-notifications',
}

export type FlagContextType = Record<
  keyof typeof FlagsmithFeatureFlagMap | keyof typeof PostHogFeatureFlagMap,
  boolean
>;

// sort flags alphabetically to match flag-smith order
export const DefaultFlagsContext: FlagContextType = {
  // Permanently enabled - no longer checks Flagsmith),
  EnableManyCompanies: true,

  // Flagsmith flags
  EnableAlphaApps: false,
  EnableBetaApps: false,
  EnableFolderLocking: false,
  EnableRootFolderLocking: false,
  EnableSubscriptionEditSummary: false,
  EnableAppSetup: false,
  EnableAppSetupUIUpdates: false,

  // PostHog flags
  EnableAppSetupClientPreview: false,
  EnableBillingConsolidation: false,
  EnableInvoiceStatusNotifications: false,
};

/**
 * These flags are available on the server side, but are not used so we
 * purposefully exclude them
 */
type ClientOnlyFeatureFlagKeysType = 'EnableAppSetupClientPreview';

export const getServerFlagsContextValue = (
  flagsmithFlags: Omit<Flags, 'isFeatureEnabled'> & {
    isFeatureEnabled: (featureName: FlagsmithFeatureFlagMap) => boolean;
  },
  posthogFlags: Record<PostHogFeatureFlagMap, FeatureFlagValue>,
): Record<
  Exclude<keyof FlagContextType, ClientOnlyFeatureFlagKeysType>,
  boolean
> => ({
  // Permanently enabled - no longer checks Flagsmith
  EnableManyCompanies: DefaultFlagsContext.EnableManyCompanies,

  // Flagsmith flags
  EnableAlphaApps: flagsmithFlags.isFeatureEnabled(
    FlagsmithFeatureFlagMap.EnableAlphaApps,
  ),
  EnableBetaApps: flagsmithFlags.isFeatureEnabled(
    FlagsmithFeatureFlagMap.EnableBetaApps,
  ),
  EnableFolderLocking: flagsmithFlags.isFeatureEnabled(
    FlagsmithFeatureFlagMap.EnableFolderLocking,
  ),
  EnableRootFolderLocking: flagsmithFlags.isFeatureEnabled(
    FlagsmithFeatureFlagMap.EnableRootFolderLocking,
  ),
  EnableSubscriptionEditSummary: flagsmithFlags.isFeatureEnabled(
    FlagsmithFeatureFlagMap.EnableSubscriptionEditSummary,
  ),
  EnableAppSetup: flagsmithFlags.isFeatureEnabled(
    FlagsmithFeatureFlagMap.EnableAppSetup,
  ),
  EnableAppSetupUIUpdates: flagsmithFlags.isFeatureEnabled(
    FlagsmithFeatureFlagMap.EnableAppSetupUIUpdates,
  ),

  // PostHog flags
  EnableBillingConsolidation: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableBillingConsolidation],
  ),
  EnableInvoiceStatusNotifications: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableInvoiceStatusNotifications],
  ),
});

export const getClientFlagsContextValue = (
  flagsmithFlags: Omit<typeof IFlagsmith, 'hasFeature'> & {
    hasFeature: (featureName: FlagsmithFeatureFlagMap) => boolean;
  },
  posthogFlags: PostHog,
): Record<keyof FlagContextType, boolean | undefined> => ({
  // Permanently enabled - no longer checks Flagsmith
  EnableManyCompanies: DefaultFlagsContext.EnableManyCompanies,

  // Flagsmith flags
  EnableAlphaApps: flagsmithFlags.hasFeature(
    FlagsmithFeatureFlagMap.EnableAlphaApps,
  ),
  EnableBetaApps: flagsmithFlags.hasFeature(
    FlagsmithFeatureFlagMap.EnableBetaApps,
  ),
  EnableFolderLocking: flagsmithFlags.hasFeature(
    FlagsmithFeatureFlagMap.EnableFolderLocking,
  ),
  EnableRootFolderLocking: flagsmithFlags.hasFeature(
    FlagsmithFeatureFlagMap.EnableRootFolderLocking,
  ),
  EnableSubscriptionEditSummary: flagsmithFlags.hasFeature(
    FlagsmithFeatureFlagMap.EnableSubscriptionEditSummary,
  ),
  EnableAppSetup: flagsmithFlags.hasFeature(
    FlagsmithFeatureFlagMap.EnableAppSetup,
  ),
  EnableAppSetupUIUpdates: flagsmithFlags.hasFeature(
    FlagsmithFeatureFlagMap.EnableAppSetupUIUpdates,
  ),

  // PostHog flags
  EnableBillingConsolidation: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableBillingConsolidation,
  ),
  EnableAppSetupClientPreview: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableAppSetupClientPreview,
  ),
  EnableInvoiceStatusNotifications: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableInvoiceStatusNotifications,
  ),
});

export const FlagsContext = React.createContext(DefaultFlagsContext);
